import {
	Container,
	Button,
	Stack,
	Typography,
	TextField,
	Card,
	CardContent,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	Box,
	Chip,
	CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState } from "react";
import { db } from "../firebase";
import { doc, runTransaction } from "firebase/firestore";
import EditIcon from "@mui/icons-material/Edit";
import AddCardIcon from "@mui/icons-material/AddCard";
import CreditCardIcon from "@mui/icons-material/CreditCard";

interface SeasonCard {
	cardHolderName: string;
	cardIdDecimal: string;
	numEdits: number;
	daysUsed: number;
	registeredAt?: Date | { toDate: () => Date }; // Firestore Timestamp or Date
}

// Check if card name can still be edited (within 24 hours of registration)
function canEditName(card: SeasonCard): boolean {
	if (!card.registeredAt) return true; // Legacy cards without timestamp can be edited
	const registeredDate =
		"toDate" in card.registeredAt
			? card.registeredAt.toDate()
			: new Date(card.registeredAt);
	const now = new Date();
	const hoursSinceRegistration =
		(now.getTime() - registeredDate.getTime()) / (1000 * 60 * 60);
	return hoursSinceRegistration < 24;
}

// Get remaining time for name edit
function getRemainingEditTime(card: SeasonCard): string {
	if (!card.registeredAt) return "";
	const registeredDate =
		"toDate" in card.registeredAt
			? card.registeredAt.toDate()
			: new Date(card.registeredAt);
	const deadline = new Date(registeredDate.getTime() + 24 * 60 * 60 * 1000);
	const now = new Date();
	const remainingMs = deadline.getTime() - now.getTime();
	if (remainingMs <= 0) return "";
	const hours = Math.floor(remainingMs / (1000 * 60 * 60));
	const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
	return `${hours}t ${minutes}m`;
}

// Calculate max cards based on mandatory rosters
function getMaxCards(mandatoryRosters: number): number {
	if (mandatoryRosters <= 1) return 0;
	if (mandatoryRosters === 2) return 1;
	if (mandatoryRosters === 3) return 2;
	if (mandatoryRosters >= 4) return 5;
	return 0;
}

// Validate card ID (10 decimal digits)
function isValidCardId(cardId: string): boolean {
	return /^\d{10}$/.test(cardId);
}

export default function Activate() {
	const navigate = useNavigate();
	const { profile, currentUser } = useAuth();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editIndex, setEditIndex] = useState<number | null>(null);
	const [cardHolderName, setCardHolderName] = useState("");
	const [cardIdDecimal, setCardIdDecimal] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	const mandatoryRosters = profile?.mandatoryRosters ?? 0;
	const maxCards = getMaxCards(mandatoryRosters);
	const cards: SeasonCard[] = profile?.seasonCards ?? [];
	const canAddMore = cards.length < maxCards;

	const handleOpenAddDialog = () => {
		setEditIndex(null);
		setCardHolderName("");
		setCardIdDecimal("");
		setError("");
		setDialogOpen(true);
	};

	const handleOpenEditDialog = (index: number) => {
		const card = cards[index];
		setEditIndex(index);
		setCardHolderName(card.cardHolderName);
		setCardIdDecimal(card.cardIdDecimal);
		setError("");
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditIndex(null);
		setCardHolderName("");
		setCardIdDecimal("");
		setError("");
	};

	const handleSave = async () => {
		// Validate inputs
		if (!cardHolderName.trim()) {
			setError("Vennligst fyll inn navn på kortholderen.");
			return;
		}
		if (!isValidCardId(cardIdDecimal)) {
			setError("Kortnummeret må være nøyaktig 10 siffer.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const profileRef = doc(db, "profiles", currentUser.uid);

			await runTransaction(db, async (transaction) => {
				const profileDoc = await transaction.get(profileRef);
				if (!profileDoc.exists()) {
					throw new Error("Profil ikke funnet.");
				}

				const data = profileDoc.data();
				const currentCards: SeasonCard[] = data.seasonCards ?? [];
				const currentMandatory = data.mandatoryRosters ?? 0;
				const currentMaxCards = getMaxCards(currentMandatory);

				if (editIndex !== null) {
					// Editing existing card
					if (editIndex >= currentCards.length) {
						throw new Error("Kortet finnes ikke.");
					}

					const existingCard = currentCards[editIndex];
					const nameChanged =
						existingCard.cardHolderName !== cardHolderName.trim();

					// Check if name is being changed and if it's allowed
					if (nameChanged && !canEditName(existingCard)) {
						throw new Error(
							"Navn på kortholder kan kun endres inntil 24 timer etter registrering."
						);
					}

					// Check if card ID is already used by another card
					const isDuplicate = currentCards.some(
						(c, i) =>
							i !== editIndex && c.cardIdDecimal === cardIdDecimal
					);
					if (isDuplicate) {
						throw new Error(
							"Dette kortnummeret er allerede registrert."
						);
					}

					currentCards[editIndex] = {
						...existingCard,
						cardHolderName: cardHolderName.trim(),
						cardIdDecimal: cardIdDecimal,
					};
				} else {
					// Adding new card
					if (currentCards.length >= currentMaxCards) {
						throw new Error(
							"Du har allerede registrert maksimalt antall kort."
						);
					}

					// Check if card ID is already used
					const isDuplicate = currentCards.some(
						(c) => c.cardIdDecimal === cardIdDecimal
					);
					if (isDuplicate) {
						throw new Error(
							"Dette kortnummeret er allerede registrert."
						);
					}

					currentCards.push({
						cardHolderName: cardHolderName.trim(),
						cardIdDecimal: cardIdDecimal,
						numEdits: 0,
						daysUsed: 0,
						registeredAt: new Date(),
					});
				}

				transaction.update(profileRef, {
					seasonCards: currentCards,
					updated: new Date(),
				});
			});

			handleCloseDialog();
		} catch (err: any) {
			setError(err.message || "Noe gikk galt. Prøv igjen.");
		} finally {
			setLoading(false);
		}
	};

	if (mandatoryRosters < 2) {
		return (
			<Container maxWidth="sm">
				<Stack sx={{ p: 3, gap: 2 }}>
					<Alert severity="info">
						Du har ikke kjøpt sesongkort ennå. Kjøp sesongkort for å
						kunne registrere kortene dine her.
					</Alert>
					<Button
						variant="contained"
						onClick={() =>
							navigate("/store?showTypes=seasonTicket")
						}
					>
						Gå til butikken
					</Button>
					<Button variant="outlined" onClick={() => navigate("/")}>
						Tilbake
					</Button>
				</Stack>
			</Container>
		);
	}

	return (
		<Container maxWidth="sm">
			<Stack sx={{ p: 2, gap: 3 }}>
				<Box>
					<Typography variant="h5" gutterBottom>
						Mine sesongkort
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Du kan registrere opptil {maxCards} kort. Registrerte:{" "}
						{cards.length}/{maxCards}
					</Typography>
				</Box>

				{cards.length === 0 ? (
					<Alert severity="info">
						Du har ikke registrert noen kort ennå. Trykk på knappen
						under for å legge til ditt første kort.
					</Alert>
				) : (
					<Stack gap={2}>
						{cards.map((card, index) => (
							<Card key={index} variant="outlined">
								<CardContent>
									<Stack
										direction="row"
										alignItems="center"
										justifyContent="space-between"
										mb={1}
									>
										<Stack
											direction="row"
											alignItems="center"
											gap={1}
										>
											<CreditCardIcon color="primary" />
											<Typography variant="h6">
												{card.cardHolderName}
											</Typography>
										</Stack>
										<IconButton
											size="small"
											onClick={() =>
												handleOpenEditDialog(index)
											}
											title="Rediger kort"
										>
											<EditIcon />
										</IconButton>
									</Stack>
									<Typography
										variant="body2"
										color="text.secondary"
										fontFamily="monospace"
									>
										Kortnummer: {card.cardIdDecimal}
									</Typography>
									<Stack
										direction="row"
										gap={1}
										mt={1}
										flexWrap="wrap"
									>
										<Chip
											size="small"
											label={`${card.daysUsed} dager brukt`}
											color="primary"
											variant="outlined"
										/>
										{canEditName(card) ? (
											<Chip
												size="small"
												label={`Navn kan endres: ${getRemainingEditTime(
													card
												)}`}
												color="success"
												variant="outlined"
											/>
										) : (
											<Chip
												size="small"
												label="Navn låst"
												color="default"
												variant="outlined"
											/>
										)}
									</Stack>
								</CardContent>
							</Card>
						))}
					</Stack>
				)}

				{canAddMore && (
					<Button
						variant="contained"
						startIcon={<AddCardIcon />}
						onClick={handleOpenAddDialog}
						size="large"
					>
						Legg til kort
					</Button>
				)}

				<Button variant="outlined" onClick={() => navigate("/")}>
					Tilbake
				</Button>

				{/* Add/Edit Dialog */}
				<Dialog
					open={dialogOpen}
					onClose={handleCloseDialog}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>
						{editIndex !== null
							? "Rediger kort"
							: "Legg til nytt kort"}
					</DialogTitle>
					<DialogContent>
						<Stack gap={2} mt={1}>
							{error && <Alert severity="error">{error}</Alert>}
							<TextField
								label="Navn på kortholder"
								value={cardHolderName}
								onChange={(e) =>
									setCardHolderName(e.target.value)
								}
								fullWidth
								autoFocus
								placeholder="F.eks. Ola Nordmann"
							/>
							<TextField
								label="Kortnummer (10 siffer)"
								value={cardIdDecimal}
								onChange={(e) => {
									// Only allow digits and max 10 characters
									const val = e.target.value
										.replace(/\D/g, "")
										.slice(0, 10);
									setCardIdDecimal(val);
								}}
								fullWidth
								placeholder="0000000000"
								inputProps={{
									maxLength: 10,
									inputMode: "numeric",
									pattern: "[0-9]*",
								}}
								helperText={`${cardIdDecimal.length}/10 siffer`}
								error={
									cardIdDecimal.length > 0 &&
									cardIdDecimal.length !== 10
								}
							/>
							{editIndex !== null &&
								!canEditName(cards[editIndex]) && (
									<Alert severity="info">
										Navn på kortholder kan kun endres inntil
										24 timer etter registrering. Du kan
										fortsatt endre kortnummeret.
									</Alert>
								)}
							{editIndex !== null &&
								canEditName(cards[editIndex]) && (
									<Alert severity="info">
										Du kan endre navn i{" "}
										{getRemainingEditTime(cards[editIndex])}{" "}
										til.
									</Alert>
								)}
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDialog} disabled={loading}>
							Avbryt
						</Button>
						<Button
							onClick={handleSave}
							variant="contained"
							disabled={
								loading ||
								!cardHolderName.trim() ||
								!isValidCardId(cardIdDecimal)
							}
						>
							{loading ? (
								<CircularProgress size={24} />
							) : editIndex !== null ? (
								"Lagre endringer"
							) : (
								"Legg til"
							)}
						</Button>
					</DialogActions>
				</Dialog>
			</Stack>
		</Container>
	);
}
